import { useState } from "react";
import { adminQueries } from "@/api/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { PlusIcon, RefreshCw } from "lucide-react";
import { UserSelector } from "./UserSelector";
import { UserForm } from "./UserForm";
import { usePersonasQuery } from "@/api/personas";

interface User {
  username: string;
  qstarlabs_tenant: string;
  // possibly default_persona_id, etc.
}

// Transform raw tenant data into { id, name } format
function mapTenants(rawTenants: any[] = []) {
  return rawTenants.map((t) => ({
    id: t.tenant_name,
    name: t.tenant_name,
  }));
}

interface UserTabContentProps {
  username: string | null;
  onUsernameChange: (username: string) => void;
}

export function UserTabContent({
  username,
  onUsernameChange,
}: UserTabContentProps) {
  const queryClient = useQueryClient();

  // Local states
  const [isNewUser, setIsNewUser] = useState(false);

  // ===== Queries =====
  const { data: allUsers = [], isLoading: isLoadingUsers } = useQuery(
    adminQueries.users(),
  );
  const { data: rawTenants = [], isLoading: isLoadingTenants } = useQuery(
    adminQueries.tenants(),
  );
  // All personas from the server
  const { data: personas = [], isLoading: isLoadingPersonas } =
    usePersonasQuery();

  // Mapped tenants for the UserForm
  const tenants = mapTenants(rawTenants);

  // Identify which user is selected
  const selectedUser = allUsers.find((u: User) => u.username === username);

  // Filtered personas that only belong to the selected user's tenant
  const filteredPersonas = (() => {
    if (selectedUser && personas) {
      return personas.filter(
        (p) => p.qstarlabs_tenant === selectedUser.qstarlabs_tenant,
      );
    }
    return [];
  })();

  // ===== Handlers =====
  const handleCreateUser = () => {
    setIsNewUser(true);
    onUsernameChange("");
  };

  const handleRefreshData = () => {
    queryClient.invalidateQueries(adminQueries.users());
    queryClient.invalidateQueries({ queryKey: ["personas"] });
  };

  const handleUserSelect = (value: string) => {
    // 'value' is the selected username from <UserSelector/>
    onUsernameChange(value);
    setIsNewUser(false);
  };

  // Called when UserForm successfully saves or updates
  const handleSuccess = () => {
    setIsNewUser(false);
    queryClient.invalidateQueries(adminQueries.users());
    queryClient.invalidateQueries({ queryKey: ["personas"] });
  };

  const isLoading = isLoadingUsers || isLoadingTenants || isLoadingPersonas;

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <CardHeader className="px-0 pt-0">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-2xl font-semibold text-primary">
                User Management
              </CardTitle>
              <CardDescription>
                Manage users and basic account details
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleRefreshData}
                disabled={isLoading}
              >
                <RefreshCw
                  className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`}
                />
              </Button>
              <Button
                onClick={handleCreateUser}
                className="gap-1"
                disabled={isLoading}
              >
                <PlusIcon className="h-5 w-5" />
                Add New User
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="px-0">
          <div className="space-y-6">
            {/* User Selection */}

            {isNewUser ? (
              // Create new user
              <UserForm
                isNewUser
                tenants={tenants}
                personas={filteredPersonas}
                onSuccess={handleSuccess}
                handleback={() => setIsNewUser(false)}
              />
            ) : (
              <>
                {/* Choose existing user */}
                <div>
                  <Label>Select User</Label>
                  <UserSelector
                    users={allUsers}
                    selectedUser={selectedUser}
                    onSelect={handleUserSelect}
                  />
                </div>

                {/* If a user is selected, show their form */}
                {selectedUser && (
                  <UserForm
                    key={selectedUser.username}
                    isNewUser={false}
                    initialUser={selectedUser}
                    tenants={tenants}
                    personas={filteredPersonas}
                    onSuccess={handleSuccess}
                  />
                )}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
